/**
 * parseJSONIfNeeded:
 * Parst einen Wert zu JSON, falls es ein String ist.
 * Gibt bei Fehler ein leeres Objekt {} zurück.
 */
function parseJSONIfNeeded(maybeString) {
  if (!maybeString) return {};
  if (typeof maybeString === "object") return maybeString;
  try {
    return JSON.parse(maybeString);
  } catch (err) {
    console.error("JSON parse error:", err);
    return {};
  }
}

/**
 * extractTaskFields
 * Liest aus templateFields die Werte (name, clickup_vorlage_id, rolle).
 * Falls templateFields null/undefined => Fallbacks.
 *
 * Rückgabe: { name, clickup_vorlage_id, rolle }
 */
export function extractTaskFields(templateFields, workspaceRoles = []) {
  const fallbackName = "Unnamed Task";
  const fallbackClickUp = "no-clickup-id";
  const fallbackRole = "no-role";
  const fallbackDurationDays = 1;

  if (!templateFields) {
    return {
      name: fallbackName,
      clickup_vorlage_id: fallbackClickUp,
      rolle: fallbackRole,
      duration_days: fallbackDurationDays
    };
  }

  const duration_days =
      typeof templateFields.duration_days === "number" && templateFields.duration_days !== null
      ? templateFields.duration_days
      : duration_days; 

  const name =
    typeof templateFields.name === "string" && templateFields.name.length > 0
      ? templateFields.name
      : fallbackName;

  const clickup_vorlage_id =
    templateFields.clickup_tmpid ||
    templateFields.clickup_vorlage_id ||
    fallbackClickUp;

  let rolle = fallbackRole;
  if (Array.isArray(templateFields.role_id) && templateFields.role_id.length > 0) {
    const roleId = templateFields.role_id[0];
    const foundRole = workspaceRoles.find((r) => r.id === roleId);
    if (foundRole && foundRole.fields && foundRole.fields.name) {
      rolle = foundRole.fields.name;
    }
  }

  return { name, clickup_vorlage_id, rolle, duration_days };
}

// --------------------------------------------------------------
// --------------------- NEUE ZENTRALE BUILDER ------------------
// --------------------------------------------------------------

/**
 * buildTaskBlock:
 * Zentrale Funktion zum Bauen eines einzelnen Task-Objekts
 * aus möglichen Template-Feldern und Positions-/Identifikationsinfos.
 */
function buildTaskBlock(
  taskFields,      // object oder null
  instance_id,
  instance_pos,
  template_id,
  workspaceRoles
) {
  // Falls keine Felder => Fallback
  if (!taskFields) {
    return {
      type: "task",
      instance_id,
      template_id,
      instance_pos,
      name: "missing-task-template",
      clickup_vorlage_id: "no-clickup-id",
      rolle: "no-role",
    };
  }

  // Aus den Feldern extrahieren
  const { name, clickup_vorlage_id, rolle, duration_days } = extractTaskFields(
    taskFields,
    workspaceRoles
  );

  return {
    type: "task",
    instance_id,
    template_id,
    instance_pos,
    name,
    clickup_vorlage_id,
    rolle,
    duration_days,
  };
}

/**
 * buildGroupBlock:
 * Zentrale Funktion zum Bauen eines Gruppen-Objekts.
 * Nimmt groupFields (oder null), liest dort das vorlageJSON
 * und baut daraus u.a. Kinder-Tasks auf.
 *
 * Optional kann man overrideChildren mitgeben, um Kinder
 * separat zu definieren (z.B. beim "editGroup"-Modus).
 */
function buildGroupBlock(
  groupFields,         // object oder null
  instance_id,
  instance_pos,
  template_id,
  allTaskTemplates,    // Array aller Task-Vorlagen
  workspaceRoles,
  overrideChildren = null
) {
  // Fallback, falls komplette Group-Fields fehlen
  if (!groupFields) {
    return {
      type: "group",
      instance_id,
      template_id,
      instance_pos,
      name: "missing-group-template",
      children_connections: [],
      children_blocks: [],
    };
  }

  // Gruppenname
  const groupName = groupFields.name || "Unbenannte Gruppe";

  // Falls keine speziellen Children übergeben => aus vorlageJSON lesen
  let childrenArr = [];
  let childrenConnections = [];

  if (!overrideChildren) {
    const groupJson = parseJSONIfNeeded(groupFields.vorlageJSON);

    // Alte vs. neue Struktur unterscheiden
    if (groupJson?.blocks) {
      // Neue Struktur
      const groupBlockInTemplate = groupJson.blocks.find((b) => b.type === "group");
      if (groupBlockInTemplate?.children_connections) {
        childrenConnections = groupBlockInTemplate.children_connections;
      }
      const childTasks = groupJson.blocks.filter((b) => b.type === "task") || [];
      childrenArr = childTasks.map((ct) => {
        return {
          instance_id: ct.instance_id,
          instance_pos: ct.instance_pos || { x: 0, y: 0 },
          template_id: ct.template_id,
        };
      });
    } else {
      // Alte Struktur
      childrenConnections = groupJson?.children_connections || [];
      const childTasks = groupJson?.children || [];
      childrenArr = childTasks.map((ct) => ({
        instance_id: ct.instance_id,
        instance_pos: ct.instance_pos || { x: 0, y: 0 },
        template_id: ct.template_id,
      }));
    }
  } else {
    // overrideChildren wurde explizit angegeben (z.B. editGroup)
    childrenArr = overrideChildren;
  }

  // Jetzt bauen wir für jedes Kind den passenden Task-Block
  const resolvedChildTasks = childrenArr.map((child) => {
    const childTemplate = allTaskTemplates.find(
      (t) => t.id === child.template_id
    );
    const childFields = childTemplate?.fields || null;

    return buildTaskBlock(
      childFields,
      child.instance_id,
      child.instance_pos,
      child.template_id,
      workspaceRoles
    );
  });

  return {
    type: "group",
    instance_id,
    template_id,
    instance_pos,
    name: groupName,
    children_connections: childrenConnections,
    children_blocks: resolvedChildTasks,
  };
}

// --------------------------------------------------------------
// --------------------- HAUPTFUNKTION --------------------------
// --------------------------------------------------------------

/**
 * translateFromRawTamplateToStaticJson:
 * Liest DB-Felder, Tasks, Gruppen etc. und baut daraus ein
 * "staticJSON", das in "blocks" + "lvl1_connections" (wenn Flow)
 * strukturiert ist.
 *
 * modus: "editFlow" oder "editGroup"
 */
export function translateFromRawTamplateToStaticJson(
  modus,
  rawTmpDict,
  tasks = [],
  groups = [],
  roles = []
) {
  console.log("starte translation: ", rawTmpDict);

  if (!rawTmpDict || !rawTmpDict.fields) return null;

  const { id, fields } = rawTmpDict;
  const { name } = fields;
  const vorlageJSON = parseJSONIfNeeded(fields.vorlageJSON);

  const staticJSON = {
    id,
    name,
    lvl1_connections: vorlageJSON?.lvl1_connections || [],
    blocks: [],
  };

  // -------------------------------------------------------------
  // FALL 1: editFlow
  // -------------------------------------------------------------
  if (modus === "editFlow") {
    const blocksArr = vorlageJSON?.blocks || [];

    const newBlocks = blocksArr.map((block) => {
      // Gruppen-Block?
      if (block.type === "group") {
        // Gruppenvorlage suchen
        const groupTemplate = groups.find((g) => g.id === block.template_id);
        const groupFields = groupTemplate?.fields || null;

        return buildGroupBlock(
          groupFields,
          block.instance_id,
          block.instance_pos || { x: 0, y: 0 },
          groupTemplate?.id || block.template_id,
          tasks,
          roles
        );
      }
      // Task-Block?
      else if (block.type === "task") {
        // Taskvorlage suchen
        const taskTemplate = tasks.find((t) => t.id === block.template_id);
        const taskFields = taskTemplate?.fields || null;

        return buildTaskBlock(
          taskFields,
          block.instance_id,
          block.instance_pos || { x: 0, y: 0 },
          taskTemplate?.id || block.template_id,
          roles
        );
      }
      // Fallback
      else {
        return {
          type: block.type || "unknown",
          instance_id: block.instance_id || "no-instance",
        };
      }
    });

    staticJSON.blocks = newBlocks;
  }
  // -------------------------------------------------------------
  // FALL 2: editGroup
  // -------------------------------------------------------------
  else if (modus === "editGroup") {
    const groupChildrenArr = vorlageJSON?.children || [];
    const groupConnections = vorlageJSON?.children_connections || [];

    // Ein einzelner "group"-Block, der die Kinder enthält
    const groupBlock = buildGroupBlock(
      fields,            // dieselben Felder wie rawTmpDict
      "inst_" + Date.now(), // neue Instance-ID
      { x: 0, y: 0 },
      id,
      tasks,
      roles,
      groupChildrenArr   // overrideChildren für "editGroup"
    );

    // connections ggf. überschreiben
    groupBlock.children_connections = groupConnections;

    // alles in staticJSON
    staticJSON.blocks = [groupBlock];
  }

  console.log("Zusammengepuzzeltes JSON:", staticJSON);
  return staticJSON;
}

// --------------------------------------------------------------
// --------------------- ADD-NODE-LOGIK -------------------------
// --------------------------------------------------------------

// Neue Hilfsfunktion zum Generieren einer eindeutigen ID
function generateUniqueId() {
  return "inst_" + Date.now() + Math.random().toString(36).substr(2, 9);
}

// Funktion zum Klonen eines Blocks mit neuen IDs
function cloneBlockWithNewId(block, groupId) {
  const newBlock = JSON.parse(JSON.stringify(block)); // Deep Clone
  const newId = generateUniqueId();
  newBlock.instance_id = newId;
  
  // Wenn es ein Group-Block ist, müssen auch alle children_blocks neue IDs bekommen
  if (newBlock.type === "group") {
    const oldToNewIds = new Map(); // Mapping alte -> neue IDs
    
    // Neue IDs für alle Kinder
    newBlock.children_blocks = newBlock.children_blocks.map(child => {
      const oldId = child.instance_id;
      const newChildId = generateUniqueId();
      oldToNewIds.set(oldId, newChildId);
      return { ...child, instance_id: newChildId };
    });

    // Connections innerhalb der Gruppe aktualisieren
    newBlock.children_connections = (newBlock.children_connections || []).map(conn => ({
      from: oldToNewIds.get(conn.from) || conn.from,
      to: oldToNewIds.get(conn.to) || conn.to
    }));
  }

  return newBlock;
}

/**
 * funcHandleAddNode:
 * Fügt dem staticJSON einen neuen Knoten (Task oder Group) hinzu.
 * Wenn type="group", werden direkt Kind-Tasks (gemäß buildGroupBlock) eingelesen.
 */
export function funcHandleAddNode(
  template,
  type,
  currentEditingGroupId,
  popupPosition,
  setStaticJSON,
  staticJSON,
  workspaceRoles,
  tasks
) {
  console.log(
    "funcHandleAddNode: add new Block:",
    template,
    "type:",
    type,
    " group:",
    currentEditingGroupId
  );

  if (!staticJSON) {
    console.warn("staticJSON ist null/undefined. Abbruch");
    return;
  }

  // 2) Bestimme Position
  let newPos = { x: 300, y: 200 };
  if (currentEditingGroupId == null) {
    newPos = popupPosition
      ? { x: popupPosition.x, y: popupPosition.y }
      : { x: 300, y: 200 };
  } else {
    const parent = staticJSON.blocks.find(
      (b) => b.type === "group" && b.instance_id === currentEditingGroupId
    );
    if (parent && parent.instance_pos) {
      newPos = { x: parent.instance_pos.x + 50, y: parent.instance_pos.y + 50 };
    }
  }

  // 3) Task- oder Group-Block bauen (aus dem Template)
  let newBlock = null;

  if (type === "task") {
    newBlock = buildTaskBlock(
      template.fields,
      generateUniqueId(), // Neue ID für Task
      newPos,
      template.id,
      workspaceRoles
    );
  } else if (type === "group") {
    newBlock = buildGroupBlock(
      template.fields,
      generateUniqueId(), // Neue ID für Group
      newPos,
      template.id,
      tasks,
      workspaceRoles
    );
    // Neue IDs für alle Kinder und deren Verbindungen
    newBlock = cloneBlockWithNewId(newBlock);
  } else {
    console.error("Unbekannter Block-Typ:", type);
    return;
  }

  // 4) State-Update via setStaticJSON
  setStaticJSON((prev) => {
    if (!prev) return prev;

    const updated = JSON.parse(JSON.stringify(prev)); // Deep Clone

    // Top-Level oder in children_blocks einer Gruppe?
    if (!currentEditingGroupId) {
      updated.blocks.push(newBlock);
    } else {
      const parentGroup = updated.blocks.find(
        (b) => b.type === "group" && b.instance_id === currentEditingGroupId
      );
      if (!parentGroup) {
        console.warn("Eltern-Gruppe nicht gefunden:", currentEditingGroupId);
        return prev;
      }
      parentGroup.children_blocks = parentGroup.children_blocks || [];
      newBlock.parent = parentGroup.instance_id;
      parentGroup.children_blocks.push(newBlock);
    }

    return updated;
  });
}

////////////////////////////////////////////////////////////////////////////////
// Hinweis: Durch dieses refaktorisierte Vorgehen findet die Suche nach
// Vorlagen und das Ersetzen von Fallback-Werten nur noch an zentralen Stellen
// (buildTaskBlock / buildGroupBlock) statt. So muss man Änderungen zum Beispiel
// beim Fallback oder bei der Rollen-Logik nur noch einmal pflegen.  
////////////////////////////////////////////////////////////////////////////////